// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import MobiusLink from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import xIconsObject from "@synd/XDL/Icons/xngCommonIcons";
import { createLinkIconStyles, getSystemColor, mergeWithCss } from "@synd/XDL/Utilities";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    destination = "destination",
    overrideTitle = "overrideTitle",
    customCSS = "customCSS",
    // XNG-Change
    icon = "icon",
    iconPosition = "iconPosition",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.destination]: LinkFieldValue;
        [fields.overrideTitle]: string;
        [fields.customCSS]: string;
        // XNG-Change
        [fields.icon]: string;
        [fields.iconPosition]: "left" | "right";
    };
}

const linkStyles: InjectableCss = {};

export const Link: FC<OwnProps> = ({ fields }) => {
    const { title, url } = useGetLink(fields.destination);

    const styles = useMergeStyles("link", linkStyles);

    const generatedStyles = React.useMemo(() => {
        return fields.icon
            ? mergeWithCss(
                  styles,
                  createLinkIconStyles({
                      icon: xIconsObject[fields.icon],
                      linkStyles: {
                          icon: { position: fields.iconPosition || "left" },
                      },
                      variant: "primary",
                      sizeVariant: "small",
                  }),
              )
            : styles;
    }, [styles, fields]);

    if (!url) {
        return null;
    }

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <MobiusLink
                className="link"
                // {...styles}
                href={url}
                // XNG-Change
                {...generatedStyles}
            >
                {fields.overrideTitle || title || url}
            </MobiusLink>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.link {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: Link,
    definition: {
        group: "Basic",
        icon: "link",
        fieldDefinitions: [
            {
                name: fields.destination,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "Contextual",
                isRequired: true,
                tab: basicTab,
            },
            {
                name: fields.overrideTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                tab: basicTab,
            },
            // XNG-Change

            {
                name: fields.icon,
                editorTemplate: "IconField" as any,
                defaultValue: "",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.iconPosition,
                editorTemplate: "DropDownField",
                options: [
                    {
                        value: "left",
                        displayName: "Left",
                    },
                    { value: "right", displayName: "Right" },
                ],
                defaultValue: "",
                fieldType: "General",
                isVisible: item => item.fields.icon,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
